import { useState } from "react";
import type { Order, OrderItem } from "@aurelia/types";
import { Badge, toast } from "@aurelia/ui";
import { useRequestReturn } from "../../features/orders/queries";

const RETURN_STATUS_TONE = {
  REQUESTED: "accent",
  APPROVED: "success",
  REJECTED: "danger",
  COMPLETED: "neutral"
} as const;

export function OrderReturnControl({ order, item }: { order: Order; item: OrderItem }) {
  const requestReturn = useRequestReturn();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  if (order.status !== "DELIVERED" || !item.isReturnable) return null;

  if (item.returnRequest) {
    return (
      <div className="mt-2 flex flex-col gap-1">
        <Badge tone={RETURN_STATUS_TONE[item.returnRequest.status]} className="w-fit">
          Return {item.returnRequest.status.toLowerCase()}
        </Badge>
        {item.returnRequest.adminNote && <p className="text-xs text-ink-muted">Store note: {item.returnRequest.adminNote}</p>}
      </div>
    );
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="mt-2 min-h-11 text-sm font-medium text-accent hover:underline">
        Request return
      </button>
    );
  }

  return (
    <div className="mt-3 flex max-w-lg flex-col gap-2">
      <label className="text-xs font-medium text-ink-muted" htmlFor={`return-${item.id}`}>Return reason</label>
      <textarea
        id={`return-${item.id}`}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Tell us why you're returning this item"
        rows={3}
        className="rounded-xl border border-border bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-muted/60 focus:outline-none focus:ring-2 focus:ring-accent/40"
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={requestReturn.isPending || reason.trim().length < 3}
          onClick={() => requestReturn.mutate(
            { orderId: order.id, orderItemId: item.id, reason },
            {
              onSuccess: () => {
                toast("Return request submitted", "success");
                setOpen(false);
                setReason("");
              },
              onError: () => toast("Couldn't submit the return request. Please try again.", "danger")
            }
          )}
          className="h-11 rounded-full bg-ink px-4 text-sm font-medium text-canvas disabled:opacity-40 dark:bg-accent dark:text-ink"
        >
          {requestReturn.isPending ? "Submitting…" : "Submit request"}
        </button>
        <button type="button" onClick={() => { setOpen(false); setReason(""); }} className="h-11 rounded-full px-4 text-sm font-medium text-ink-muted hover:text-ink">
          Cancel
        </button>
      </div>
    </div>
  );
}
